/*
 * @license
 * Copyright Endlessjs. All Rights Reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 */

import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  HostBinding,
  Inject,
  OnInit,
  OnDestroy,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { filter, takeWhile } from 'rxjs/operators';

import { ElLayoutDirectionService } from '../../services/direction.service';
import { EL_WINDOW } from '../../theme.options';
import { ElCellDirective, ElFooterCellDirective, ElHeaderCellDirective } from '../cdk/table/cell';
import { ElCdkCell, ElCdkFooterCell, ElCdkHeaderCell } from '../cdk/table/type-mappings';
import { EL_TREE_GRID } from './tree-grid-injection-tokens';
import { ElTreeGridComponent } from './tree-grid.component';
import { ElTreeGridColumnDefDirective } from './tree-grid-column-def.directive';
import { EL_DEFAULT_ROW_LEVEL } from './data-source/tree-grid.model';
import { ElColumnsService } from './tree-grid-columns.service';

@Directive({
  selector: 'td[elTreeGridCell]',
  host: {
    'class': 'el-tree-grid-cell',
    'role': 'gridcell',
  },
  providers: [{ provide: ElCdkCell, useExisting: ElTreeGridCellDirective }],
})
export class ElTreeGridCellDirective extends ElCellDirective implements OnInit, OnDestroy {
  private alive: boolean = true;
  private readonly tree: ElTreeGridComponent<any>;
  private readonly columnDef: ElTreeGridColumnDefDirective;
  private initialLeftPadding: string = '';
  private initialRightPadding: string = '';
  private latestWidth: string;
  elementRef: ElementRef<HTMLElement>;

  @HostBinding('style.width')
  get columnWidth(): string {
    this.latestWidth = this.tree.getColumnWidth();
    return this.latestWidth || null;
  }

  @HostBinding('style.padding-left')
  get leftPadding(): string | SafeStyle | null {
    if (this.directionService.isLtr()) {
      return this.getStartPadding();
    }
    return null;
  }

  @HostBinding('style.padding-right')
  get rightPadding(): string | SafeStyle | null {
    if (this.directionService.isRtl()) {
      return this.getStartPadding();
    }
    return null;
  }

  constructor(
    columnDef: ElTreeGridColumnDefDirective,
    elementRef: ElementRef<HTMLElement>,
    @Inject(EL_TREE_GRID) tree,
    @Inject(PLATFORM_ID) private platformId,
    @Inject(EL_WINDOW) private window,
    private sanitizer: DomSanitizer,
    private directionService: ElLayoutDirectionService,
    private columnService: ElColumnsService,
    private cd: ChangeDetectorRef,
  ) {
    super(columnDef, elementRef);
    this.tree = tree as ElTreeGridComponent<any>;
    this.columnDef = columnDef;
    this.elementRef = elementRef;
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      const style = this.window.getComputedStyle(this.elementRef.nativeElement);
      this.initialLeftPadding = style.paddingLeft;
      this.initialRightPadding = style.paddingRight;
    }

    this.columnService.onColumnsChange()
      .pipe(
        takeWhile(() => this.alive),
        filter(() => this.latestWidth !== this.tree.getColumnWidth()),
      )
      .subscribe(() => this.cd.detectChanges());
  }

  ngOnDestroy() {
    this.alive = false;
  }

  toggleRow(): void {
    this.tree.toggleCellRow(this);
  }

  private get initialStartPadding(): string {
    return this.directionService.isLtr()
      ? this.initialLeftPadding
      : this.initialRightPadding;
  }

  private getStartPadding(): string | SafeStyle | null {
    const rowLevel = this.tree.getCellLevel(this, this.columnDef.name);
    if (rowLevel === EL_DEFAULT_ROW_LEVEL) {
      return null;
    }

    const nestingLevel = rowLevel + 1;
    let padding: string = '';
    if (this.tree.levelPadding) {
      padding = `calc(${this.tree.levelPadding} * ${nestingLevel})`;
    } else if (this.initialStartPadding) {
      padding = `calc(${this.initialStartPadding} * ${nestingLevel})`;
    }

    if (!padding) {
      return null;
    }

    return this.sanitizer.bypassSecurityTrustStyle(padding);
  }
}

@Directive({
  selector: 'th[elTreeGridHeaderCell]',
  host: {
    'class': 'el-tree-grid-header-cell',
    'role': 'columnheader',
  },
  providers: [{ provide: ElCdkHeaderCell, useExisting: ElTreeGridHeaderCellDirective }],
})
export class ElTreeGridHeaderCellDirective extends ElHeaderCellDirective implements OnInit, OnDestroy {
  private alive: boolean = true;
  private latestWidth: string;
  private readonly tree: ElTreeGridComponent<any>;

  @HostBinding('style.width')
  get columnWidth(): string {
    this.latestWidth = this.tree.getColumnWidth();
    return this.latestWidth || null;
  }

  constructor(
    columnDef: ElTreeGridColumnDefDirective,
    elementRef: ElementRef<HTMLElement>,
    @Inject(EL_TREE_GRID) tree,
    private columnService: ElColumnsService,
    private cd: ChangeDetectorRef,
  ) {
    super(columnDef, elementRef);
    this.tree = tree as ElTreeGridComponent<any>;
  }

  ngOnInit() {
    this.columnService.onColumnsChange()
      .pipe(
        takeWhile(() => this.alive),
        filter(() => this.latestWidth !== this.tree.getColumnWidth()),
      )
      .subscribe(() => this.cd.detectChanges());
  }

  ngOnDestroy() {
    this.alive = false;
  }
}

@Directive({
  selector: 'td[elTreeGridFooterCell]',
  host: {
    'class': 'el-tree-grid-footer-cell',
    'role': 'gridcell',
  },
  providers: [{ provide: ElCdkFooterCell, useExisting: ElTreeGridFooterCellDirective }],
})
export class ElTreeGridFooterCellDirective extends ElFooterCellDirective implements OnInit, OnDestroy {
  private alive: boolean = true;
  private latestWidth: string;
  private readonly tree: ElTreeGridComponent<any>;

  @HostBinding('style.width')
  get columnWidth(): string {
    this.latestWidth = this.tree.getColumnWidth();
    return this.latestWidth || null;
  }

  constructor(
    columnDef: ElTreeGridColumnDefDirective,
    elementRef: ElementRef,
    @Inject(EL_TREE_GRID) tree,
    private columnService: ElColumnsService,
    private cd: ChangeDetectorRef,
  ) {
    super(columnDef, elementRef);
    this.tree = tree as ElTreeGridComponent<any>;
  }

  ngOnInit() {
    this.columnService.onColumnsChange()
      .pipe(
        takeWhile(() => this.alive),
        filter(() => this.latestWidth !== this.tree.getColumnWidth()),
      )
      .subscribe(() => this.cd.detectChanges());
  }

  ngOnDestroy() {
    this.alive = false;
  }
}
